import { GetProp, UploadFile, UploadProps } from "antd";
import moment from "moment";
import { BASIC_INFORMATION_LOAN_REQUEST_PAGE, ListFinance } from "~utils/constants/api-const";

function clone<T>(v: T): T {
  const o: any = {};
  for (let k in v) {
    o[k] = v[k];
  }
  return o;
}

/**
 * Base64とMIMEコンテンツタイプからBlobオブジェクトを作成する。
 * 日本語対応。
 *
 * @param base64
 * @param mime_ctype MIMEコンテンツタイプ
 * @returns Blob
 */
function toBlob(base64: string, mime_ctype: string) {
  // 日本語の文字化けに対処するためBOMを作成する。
  var bom = new Uint8Array([0xef, 0xbb, 0xbf]);

  var bin = atob(base64.replace(/^.*,/, ""));
  var buffer = new Uint8Array(bin.length);
  for (var i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i);
  }
  // Blobを作成
  try {
    var blob = new Blob([bom, buffer.buffer], {
      type: mime_ctype,
    });
  } catch (e) {
    return null;
  }
  return blob;
}

/**
 * Convert byte array to file
 *
 * @param byteArray
 * @param fileName
 * @param fileType
 */
function byteArrayToFile(
  byteArray: Uint8Array,
  fileName: string,
  fileType: string,
): File {
  const blob = new Blob([byteArray], { type: fileType });
  return new File([blob], fileName, { lastModified: new Date().getTime() });
}

// @ts-ignore
type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

/**
 * Convert file to byte array
 *
 * @param file
 */
function fileToByteArray(file: FileType): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        const arrayBuffer = new Uint8Array(reader.result);
        resolve(arrayBuffer);
      } else {
        reject(new Error("Failed to convert file to byte array."));
      }
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Save file to session storage
 *
 * @param file
 * @param key
 */
async function saveFileToSessionStorage(file: FileType, key: string) {
  try {
    // const byteArray = await fileToByteArray(file);
    sessionStorage.setItem(
      key,
      JSON.stringify({
        name: file.name,
        type: file.type,
        data: Array.from(""),
      }),
    );
  } catch (error) {
    console.error("Failed to save file to session storage:", error);
  }
}

/**
 * Get file from session storage
 *
 * @param key
 */
function getFileFromSessionStorage(key: string) {
  const data = sessionStorage.getItem(key);
  if (data) {
    const { name, type, data: byteArray } = JSON.parse(data);
    return byteArrayToFile(new Uint8Array(byteArray), name, type);
  }
  return null;
}

/**
 * Get session list
 *
 * @param sessionKey
 */
function getSessionList(sessionKey: string) {
  let listFile: UploadFile[] = [];
  const list = sessionStorage.getItem(sessionKey);
  if (list) {
    const fileList = JSON.parse(list);
    fileList.forEach((value: string) => {
      const file = getFileFromSessionStorage(value);
      if (file) {
        listFile = [...listFile, file as any];
      }
    });
  }
  return listFile;
}

/**
 * Save file list session
 *
 * @param fileList
 * @param sessionKey
 */
function saveFileListToSession(fileList: UploadFile[], sessionKey: string) {
  if (fileList.length) {
    const listFileNames: string[] = [];
    fileList.forEach((file, index) => {
      const name = `${sessionKey}_${index}`;
      saveFileToSessionStorage(file as FileType, name);
      listFileNames.push(name);
    });
    sessionStorage.setItem(sessionKey, JSON.stringify(listFileNames));
  }
}

/**
 * Remove session list
 *
 * @param sessionKey
 */
function removeSessionList(sessionKey: string) {
  const list = sessionStorage.getItem(sessionKey);
  if (list) {
    const fileList = JSON.parse(list);
    fileList.forEach((value: string) => {
      sessionStorage.removeItem(value);
    });
    sessionStorage.removeItem(sessionKey);
  }
}

/**
 * File to base64
 *
 * @param file
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Is filter attribute
 */
function isFilterAttribute(
  name: string,
  loanAttributes: any,
  loanFilter: any,
  bankCode: string,
  financeTypeId: string,
  typeShow: string,
): boolean {
  const keysInList: string[] = Object.keys(loanAttributes);

  let financingCode = "default";
  if (bankCode && bankCode in loanFilter) {
    financingCode = bankCode;
  }

  let financeType = financeTypeId;

  if (!financeType || !(loanFilter[financingCode].finance_type.includes(Number.parseInt(financeType)))) {
    financeType = loanFilter[financingCode].finance_type[0];
  }

  if (financeType) {
    return keysInList.some(
      (key) =>
        loanFilter[financingCode][financeType][typeShow].includes(
          parseInt(key),
        ) && loanAttributes[key] === name,
    );
  }

  return false;
}

/**
 * Get Tool tip
 *
 * @param name
 * @param loanAttributes
 * @param loanFilter
 * @param bankCode
 */
function getTooltip(
  name: string,
  loanAttributes: any,
  loanFilter: any,
  bankCode: string,
) {
  let title = ''
  let financingCode = "default";
  if (bankCode && bankCode in loanFilter) {
    financingCode = bankCode;
  }

  const key = findKeyByValue(loanAttributes, name);
  if (key) {
    if (key in loanFilter[financingCode]['tooltips_list']) {
      title = loanFilter[financingCode]['tooltips_list'][key];

      if (!title.length) {
        title = ''
      }
    }
  }

  return title;
}

function findKeyByValue(obj: any, value: string): string | undefined {
  return Object.keys(obj).find(key => obj[key] === value);
}

/**
 * Convert bytes
 *
 * @param bytes
 */
function bytesConvert(bytes: number): string {
  if (bytes >= 1073741824) {
    return (bytes / 1073741824).toFixed(2) + " GB";
  } else if (bytes >= 1048576) {
    return (bytes / 1048576).toFixed(2) + " MB";
  } else if (bytes >= 1024) {
    return (bytes / 1024).toFixed(2) + " KB";
  } else if (bytes > 1) {
    return bytes + " bytes";
  } else if (bytes === 1) {
    return bytes + " byte";
  } else {
    return "0 bytes";
  }
}

/**
 * Get first valid loan date
 *
 * @param holidays
 * @param preferredLoanDate
 */
function getFirstValidLoanDate(
  holidays: string[],
  preferredLoanDate: number,
): Date {
  let nextPreferredLoanDate = new Date();
  let count = 0;

  while (count < preferredLoanDate) {
    nextPreferredLoanDate.setDate(nextPreferredLoanDate.getDate() + 1);
    if (
      !isWeekend(nextPreferredLoanDate) &&
      !isHoliday(nextPreferredLoanDate, holidays)
    ) {
      count++;
    }
  }

  return nextPreferredLoanDate;
}

/**
 * Validate Preferred LoanDate
 *
 * @param selectionDate
 * @param holidays
 * @param preferredLoanDate
 * @param addDate
 */
function validatePreferredLoanDate(
  selectionDate: string | null,
  holidays: string[],
  preferredLoanDate: number,
  addDate: string,
): string | null {
  if (!selectionDate || !validateFormatTime(selectionDate)) {
    return selectionDate;
  }

  let nextPreferredLoanDate = new Date(addDate);
  let count = 1;
  while (count < preferredLoanDate) {
    nextPreferredLoanDate.setDate(nextPreferredLoanDate.getDate() + 1);
    if (
      !isWeekend(nextPreferredLoanDate) &&
      !isHoliday(nextPreferredLoanDate, holidays)
    ) {
      count++;
    }
  }

  return isHoliday(selectionDate, holidays) ||
    isWeekend(selectionDate) ||
    nextPreferredLoanDate >= new Date(selectionDate)
    ? null
    : selectionDate;
}

/**
 * Validate Format Time
 *
 * @param date
 */
function validateFormatTime(date: string | null | undefined): boolean {
  return !!(date && moment(date, "YYYY/MM/DD", true).isValid());
}

/**
 * Check is weekend
 *
 * @param date
 */
function isWeekend(date: Date | string): boolean {
  const dayOfWeek = new Date(date).getDay();
  return dayOfWeek === 6 || dayOfWeek === 0;
}

/**
 * Check is holiday
 *
 * @param date
 * @param holidays
 */
function isHoliday(
  date: string | null | undefined | Date,
  holidays: string[],
): boolean {
  if (!date) return false;
  const dateFormat = new Date(date);
  const dd = String(dateFormat.getDate()).padStart(2, "0");
  const mm = String(dateFormat.getMonth() + 1).padStart(2, "0");
  const yyyy = dateFormat.getFullYear();
  const formattedDate = `${yyyy}/${mm}/${dd}`;
  return holidays.includes(formattedDate);
}

/**
 * Validate date
 *
 * @param selectionDate
 * @param holidays
 */
function validateDate(
  selectionDate: string | null,
  holidays: string[],
): string | null {
  if (!selectionDate || !validateFormatTime(selectionDate)) {
    return selectionDate;
  }

  const isHolidayOrWeekend =
    isHoliday(selectionDate, holidays) || isWeekend(selectionDate);

  return isHolidayOrWeekend ? null : selectionDate;
}

/**
 * Format string to number
 *
 * @param input
 */
function formatStringToNumber(input: string): number {
  const cleanedInput = input.replace(/\D/g, "");
  return parseInt(cleanedInput, 10);
}

function getFinancesFromAllowBankCds(
  basicInformation: BASIC_INFORMATION_LOAN_REQUEST_PAGE | undefined,
  bankCd: string | null = null
): ListFinance[] {
  if (!basicInformation) {
    return [];
  }

  let allowBankCds = basicInformation.allow_bank_cd["default"];
  if (bankCd) {
    allowBankCds = basicInformation.allow_bank_cd[bankCd];
  }

  return allowBankCds
    ? basicInformation?.list_finance.filter(
        (item) =>
          allowBankCds.includes(item.bank_code) &&
          basicInformation.loan_filter[item.bank_code].finance_type.length !== 0
      )
    : [];
}

export {
  clone,
  toBlob,
  byteArrayToFile,
  fileToByteArray,
  saveFileToSessionStorage,
  getFileFromSessionStorage,
  getSessionList,
  saveFileListToSession,
  removeSessionList,
  fileToBase64,
  isFilterAttribute,
  bytesConvert,
  validatePreferredLoanDate,
  getFirstValidLoanDate,
  validateDate,
  formatStringToNumber,
  getTooltip,
  getFinancesFromAllowBankCds
};
